/* path: src/Steps/StepOne/Fields/DateSelector/Calendar.js */
import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment-timezone';
import 'moment/locale/nl';
import { isSameDay } from './Utils/dateUtils';
import { isDateAvailableWithTableCheck } from '../algorithm/isDateAvailableWithTableCheck';
import { getAvailableTimeblocks } from '../algorithm/getAvailableTimeblocks';
import { filterTimeblocksByMaxGroups } from '../algorithm/filters/maxGroupsFilter.js';
import { filterTimeblocksByMaxArrivals } from '../algorithm/filters/maxArrivalsFilter.js';
import { filterTimeblocksByStopTimes } from '../algorithm/filters/timeFilter.js';
import { checkYeatAvailability } from './Utils/yeatUtils';
import './css/calendar.css';
import './css/mobile.css';

// Use CommonJS require for compatibility
const { getAvailableTimeblocksWithTableCheck } = require('../algorithm/simulateTableAssignment');

moment.locale('nl');

const Calendar = ({
  selectedDate,
  onSelectDate,
  isExpanded,
  setIsExpanded,
  restaurantData,
  guests,
  reservations,
  onMonthChange,
  giftcard, // prop for giftcard selection
}) => {
  const [startDate, setStartDate] = useState(null);
  const [availableDates, setAvailableDates] = useState({}); // Cache availability results
  const [yeatOnlyDates, setYeatOnlyDates] = useState(new Set()); // Track dates that are only available via Yeat
  const calendarRef = useRef(null);
  const maxDate = moment().tz('Europe/Brussels').add(1, 'year').endOf('day');

  useEffect(() => {
    const today = moment().tz('Europe/Brussels').startOf('day');
    setStartDate(today.clone().startOf('month'));
  }, []);

  // Clear cached availability data when key dependencies change
  useEffect(() => {
    setAvailableDates({});
    setYeatOnlyDates(new Set());
  }, [guests, restaurantData, reservations, giftcard]);

  const generateCalendarGrid = (startDate) => {
    const days = [];
    const monthStart = startDate.clone().startOf('month');
    const monthEnd = startDate.clone().endOf('month');
    const startDayOfWeek = monthStart.isoWeekday();
    const endDayOfWeek = monthEnd.isoWeekday();

    // Add empty cells before the first day
    for (let i = 1; i < startDayOfWeek; i++) {
      days.push(null);
    }

    // Check if any needed data is missing
    if (!restaurantData || !guests || !reservations) {
      console.error('Calendar: Missing required data - restaurantData, guests, or reservations');
      return days;
    }

    const today = moment().tz('Europe/Brussels').startOf('day');
    const newYeatOnlyDates = new Set();

    // Helper to compute final timeblocks count for a date with a given reservations set
    const computeFinalTimeblocksCount = (dateString, reservationsArg) => {
      try {
        let tbs = getAvailableTimeblocks(
          restaurantData,
          dateString,
          reservationsArg,
          guests,
          giftcard
        );
        if (Object.keys(tbs).length === 0) return 0;
        tbs = filterTimeblocksByMaxGroups(
          restaurantData,
          dateString,
          tbs,
          reservationsArg,
          guests
        );
        if (Object.keys(tbs).length === 0) return 0;
        tbs = filterTimeblocksByMaxArrivals(
          restaurantData,
          dateString,
          tbs,
          reservationsArg,
          guests
        );
        if (Object.keys(tbs).length === 0) return 0;
        const tableSettings = restaurantData?.['table-settings'] || {};
        const isTableAssignmentEnabled =
          tableSettings.isInstalled === true && tableSettings.assignmentMode === 'automatic';
        if (isTableAssignmentEnabled) {
          tbs = getAvailableTimeblocksWithTableCheck(
            restaurantData,
            dateString,
            tbs,
            guests,
            reservationsArg
          );
          if (Object.keys(tbs).length === 0) return 0;
          tbs = filterTimeblocksByMaxArrivals(
            restaurantData,
            dateString,
            tbs,
            reservationsArg,
            guests
          );
        }
        tbs = filterTimeblocksByStopTimes(tbs, restaurantData, dateString);
        return Object.keys(tbs).length;
      } catch (e) {
        console.warn('Calendar: computeFinalTimeblocksCount failed for', dateString, e);
        return 0;
      }
    };

    // Process each day in the month
    for (
      let date = monthStart.clone();
      date.isSameOrBefore(monthEnd, 'day');
      date.add(1, 'day')
    ) {
      const formattedDate = date.format('YYYY-MM-DD');
      let isAvailable = false;
      let isYeatOnly = false;

      try {
        // Use cached result if available
        if (Object.prototype.hasOwnProperty.call(availableDates, formattedDate)) {
          isAvailable = availableDates[formattedDate];
          isYeatOnly = yeatOnlyDates.has(formattedDate);
        } else {
          // Only check future dates
          if (date.isBefore(today, 'day')) {
            isAvailable = false;
          } else {
            // First check regular availability
            isAvailable = isDateAvailableWithTableCheck(
              restaurantData,
              formattedDate,
              reservations,
              guests,
              giftcard
            );

            // If not available through regular means, check for Yeat availability (future-only for today)
            if (!isAvailable) {
              const yeatAvailable = checkYeatAvailability(reservations, formattedDate, guests);
              if (yeatAvailable) {
                isAvailable = true;
                isYeatOnly = true;
                newYeatOnlyDates.add(formattedDate);
              }
            }

            // Determine if the day is waitlist-only (no regular times but baseline has times)
            let isWaitlistOnly = false;
            if (!isAvailable) {
              const baselineCount = computeFinalTimeblocksCount(formattedDate, []);
              const currentCount = computeFinalTimeblocksCount(formattedDate, reservations);
              if (baselineCount > 0 && currentCount === 0) {
                isWaitlistOnly = true;
              }
            }

            // Cache the result
            setAvailableDates((prev) => ({
              ...prev,
              [formattedDate]: isAvailable,
            }));
            // Store waitlist-only flag by piggybacking on yeatOnlyDates set? Keep separate classification below
          }

          // Log availability result for debugging
          const availabilityType = isYeatOnly ? 'Yeat Only' : isAvailable ? 'Regular' : 'Unavailable';
          console.log(`Date ${formattedDate} availability: ${availabilityType}`);
        }
      } catch (error) {
        console.error(`Error checking availability for ${formattedDate}:`, error);
        isAvailable = false;
      }

      // Recompute waitlist-only flag for the day when adding to days[]
      let waitlistOnlyFlag = false;
      if (!isAvailable && !date.isBefore(today, 'day')) {
        try {
          const baselineCount = computeFinalTimeblocksCount(formattedDate, []);
          const currentCount = computeFinalTimeblocksCount(formattedDate, reservations);
          waitlistOnlyFlag = baselineCount > 0 && currentCount === 0;
        } catch (e) {
          waitlistOnlyFlag = false;
        }
      }

      days.push({
        date: date.clone(),
        isPast: date.isBefore(today, 'day'),
        isFuture: date.isAfter(maxDate, 'day'),
        isCurrentMonth: true,
        isAvailable,
        isYeatOnly,
        isWaitlistOnly: waitlistOnlyFlag,
      });
    }

    // Update the Yeat-only dates set
    if (newYeatOnlyDates.size > 0) {
      setYeatOnlyDates((prev) => new Set([...prev, ...newYeatOnlyDates]));
    }

    // Add empty cells after the last day
    for (let i = endDayOfWeek; i < 7; i++) {
      days.push(null);
    }

    return days;
  };

  const handleDateClick = (day) => {
    if (
      day &&
      (day.isAvailable || day.isWaitlistOnly) &&
      !day.isPast &&
      !day.isFuture &&
      day.isCurrentMonth
    ) {
      const formattedDate = day.date.format('YYYY-MM-DD');

      // Always allow date selection (including Yeat dates)
      // The TimeSelector will handle Yeat-specific behavior
      console.log(
        `Selected date: ${formattedDate}${
          day.isYeatOnly ? ' (Yeat Only)' : ''
        }${day.isWaitlistOnly ? ' (Wachtlijst Alleen)' : ''}`
      );
      onSelectDate(day.date.toDate());
      setIsExpanded(false);
    } else if (day) {
      console.log(`Clicked on unavailable date: ${day.date.format('YYYY-MM-DD')}`);
    }
  };

  const handlePrevMonth = () => {
    const newStartDate = startDate.clone().subtract(1, 'month');
    setStartDate(newStartDate);
    onMonthChange && onMonthChange(newStartDate);
  };

  const handleNextMonth = () => {
    const newStartDate = startDate.clone().add(1, 'month');
    setStartDate(newStartDate);
    onMonthChange && onMonthChange(newStartDate);
  };

  const formatDisplayDate = () => {
    if (!selectedDate) return 'Selecteer een datum';
    const selectedMoment = moment(selectedDate).tz('Europe/Brussels').startOf('day');
    const today = moment().tz('Europe/Brussels').startOf('day');
    const tomorrow = moment().tz('Europe/Brussels').add(1, 'day').startOf('day');
    if (selectedMoment.isSame(today, 'day')) return 'Vandaag';
    if (selectedMoment.isSame(tomorrow, 'day')) return 'Morgen';
    return selectedMoment.format('DD MMMM YYYY');
  };

  const days = startDate ? generateCalendarGrid(startDate) : [];
  // Break days into weeks of 7 cells
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  // Capitalize the month name
  const monthName = startDate ? startDate.format('MMMM YYYY') : '';
  const capitalizedMonthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);

  return (
    <div className="calendar-container" ref={calendarRef}>
      <div className="calendar-display" onClick={() => setIsExpanded(!isExpanded)}>
        <span>{formatDisplayDate()}</span>
        <span className="arrow">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            style={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
          </svg>
        </span>
      </div>
      {isExpanded && startDate && (
        <div className="calendar">
          <div className="calendar-header">
            <button type="button" onClick={handlePrevMonth}>
              &lt;
            </button>
            <span>{capitalizedMonthName}</span>
            <button type="button" onClick={handleNextMonth}>
              &gt;
            </button>
          </div>
          <div className="calendar-weeks-wrapper">
            <table className="calendar-table">
              <thead>
                <tr>
                  {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map((day) => (
                    <th key={day}>{day}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weeks.map((week, wIndex) => (
                  <tr key={wIndex}>
                    {week.map((dayObj, dIndex) => {
                      if (dayObj === null) {
                        return (
                          <td key={dIndex} className="empty">
                            <div className="day-square"></div>
                          </td>
                        );
                      }
                      const isSelected =
                        selectedDate &&
                        isSameDay(dayObj.date, moment(selectedDate).tz('Europe/Brussels'));
                      const classNames = [];
                      if (dayObj.isPast) {
                        classNames.push('gray-out');
                      } else if (dayObj.isAvailable) {
                        // Use same styling for both regular and Yeat dates
                        classNames.push('available');
                      } else if (dayObj.isWaitlistOnly) {
                        classNames.push('waitlist-only');
                      } else {
                        classNames.push('unavailable');
                      }
                      if (isSelected) classNames.push('selected');
                      return (
                        <td
                          key={dIndex}
                          className={classNames.join(' ')}
                          onClick={() => handleDateClick(dayObj)}
                          style={{ '--animation-order': wIndex * 7 + dIndex }}
                        >
                          <div className="day-square">{dayObj.date.date()}</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
